/**
 * Hook useReturnedMain
 * 
 * Hook centralizado que maneja toda la lógica de negocio del módulo Returned/List.
 * Sigue el patrón establecido en Orders/List para consistencia en la arquitectura.
 * 
 * Responsabilidades:
 * - Manejo de filtros, búsqueda y sorting
 * - Orquestación de datos desde useReturned
 * - Exposición de props para componentes de vista
 * - Detección de estados de filtros activos
 */

import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReturned } from './useReturned';

export const useReturnedMain = (
  searchTerm?: string,
  externalFilterConditions?: FilterConditionOutput[],
  externalSorting?: { field: string; direction: 'asc' | 'desc' } | null
) => {
  const { t } = useTranslation('returnedModule');

  // Estados de filtros (usar externos si están disponibles)
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalSorting, setInternalSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Procesar filtros para enviar al hook
  const processedFilters = useMemo(() => {
    const event_ids: number[] = [];
    const currentFilterConditions = externalFilterConditions || filterConditions;
    const currentSorting = externalSorting || internalSorting;

    currentFilterConditions.forEach((condition) => {
      if (condition.column === 'event' && condition.action === 'equals') {
        event_ids.push(Number(condition.value));
      }
    });

    return {
      ...(event_ids.length > 0 && { event_ids }),
      sortBy: currentSorting?.field ?? 'id',
      sortDirection: currentSorting?.direction ?? 'desc',
    };
  }, [externalFilterConditions, filterConditions, externalSorting, internalSorting]);

  // 🎯 Memoizar el objeto de filtros para evitar re-renders infinitos
  const queryFilters = useMemo(() => ({
    searchTerm: searchTerm || internalSearchTerm,
    ...processedFilters,
  }), [searchTerm, internalSearchTerm, processedFilters]);

  // Hook de datos con scroll infinito
  const {
    returned,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useReturned(queryFilters);

  // Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
    },
    []
  );

  const handleSearch = useCallback((term: string) => {
    setInternalSearchTerm(term);
  }, []);

  const handleSortingChange = useCallback((newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    setInternalSorting(newSorting);
  }, []);

  // Props compartidas para ambas vistas (Mobile/Desktop)
  const returnedViewProps = useMemo(
    () => ({
      returned,
      isLoading,
      isFetching, // 🎯 Añadir isFetching para control de opacidad
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting: externalSorting || internalSorting,
      onSortingChange: handleSortingChange,
    }),
    [
      returned,
      isLoading,
      isFetching, // 🎯 Añadir isFetching a las dependencias
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      externalSorting,
      internalSorting,
      handleSortingChange,
    ]
  );

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    const currentFilterConditions = externalFilterConditions || filterConditions;
    const currentSearchTerm = searchTerm || internalSearchTerm;
    const currentSorting = externalSorting || internalSorting;
    
    return (
      currentFilterConditions.length > 0 ||
      currentSearchTerm !== '' ||
      currentSorting !== null
    );
  }, [externalFilterConditions, filterConditions.length, searchTerm, internalSearchTerm, externalSorting, internalSorting]);

  return {
    // Estados
    filterConditions: externalFilterConditions || filterConditions,
    searchTerm: searchTerm || internalSearchTerm,
    sorting: externalSorting || internalSorting,

    // Datos
    returned,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    returnedViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleSortingChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  };
};

